import { Bounds, Container, Graphics, Matrix, Rectangle, getLocalBounds as pixiGetLocalBounds } from "pixi.js";

export type MaskShape = "none" | "circle" | "rounded";

type MaskState = {
    shape: MaskShape;
    cx: number;
    cy: number;
    w: number;
    h: number;
    r: number;
    invScaleX: number;
    invScaleY: number;
};

const GRAPHICS_KEY = "__librettoShapeMaskGraphics";
const STATE_KEY = "__librettoShapeMaskState";
const BOUNDS_PROXY_KEY = "__librettoShapeMaskBoundsProxy";

const _tmpBounds = new Bounds();
const _tmpMatrix = new Matrix();

function findMaskTarget(container: Container, mask: Graphics | null, boundsProxy: Container | null): Container | null {
    try {
        for (const child of container.children) {
            if (!child) continue;
            if (mask && child === mask) continue;
            if (boundsProxy && child === boundsProxy) continue;
            return child as any;
        }
    } catch {}
    return null;
}

function ensureBoundsProxy(container: Container): Container {
    let proxy: Container | null = (container as any)[BOUNDS_PROXY_KEY] ?? null;
    if (!proxy || (proxy as any).destroyed) {
        proxy = new Container();
        (container as any)[BOUNDS_PROXY_KEY] = proxy;
    }
    try {
        proxy.renderable = false;
        proxy.visible = true;
        proxy.measurable = true;
        (proxy as any).eventMode = "none";
    } catch {}
    try {
        if (proxy.parent !== container) {
            if (proxy.parent) proxy.parent.removeChild(proxy);
            container.addChild(proxy);
        }
    } catch {}
    return proxy;
}

function updateProxyBounds(proxy: Container, b: Bounds): void {
    try {
        const rect: Rectangle = (proxy.boundsArea as any) ?? new Rectangle(0, 0, 0, 0);
        rect.x = (b as any).minX ?? (b as any).x ?? 0;
        rect.y = (b as any).minY ?? (b as any).y ?? 0;
        rect.width = (b as any).width ?? 0;
        rect.height = (b as any).height ?? 0;
        proxy.boundsArea = rect;
    } catch {}
}

/**
 * Apply a simple geometric mask to a Pixi Container (clip view).
 *
 * Important detail (Pixi v8): mask effects modify local-bounds. Our editor continuously sets
 * `view.width/height`, which uses `getLocalBounds()` to compute scale. To avoid the mask resizing
 * the clip, we:
 * - apply the mask to the visible child (sprite/container), not the clip container itself
 * - add an invisible bounds-proxy child that preserves the unmasked bounds for scaling/measurement
 */
export function applyShapeMask(container: Container, shape: MaskShape | null | undefined): void {
    const normalized: MaskShape = shape === "circle" ? "circle" : shape === "rounded" ? "rounded" : "none";

    let mask: Graphics | null = (container as any)[GRAPHICS_KEY] ?? null;
    const existingProxy: Container | null = (container as any)[BOUNDS_PROXY_KEY] ?? null;

    if (normalized === "none") {
        if (!mask && !existingProxy) return;
        const target: any = findMaskTarget(container, mask, existingProxy);
        try {
            if (target && target.mask === mask) target.mask = null;
        } catch {}
        try {
            if (mask) {
                if (mask.parent) mask.parent.removeChild(mask);
                mask.destroy();
            }
        } catch {}
        try {
            if (existingProxy) {
                if (existingProxy.parent) existingProxy.parent.removeChild(existingProxy);
                existingProxy.destroy();
            }
        } catch {}
        try { (container as any)[GRAPHICS_KEY] = null; } catch {}
        try { (container as any)[STATE_KEY] = null; } catch {}
        try { (container as any)[BOUNDS_PROXY_KEY] = null; } catch {}
        return;
    }

    const boundsProxy: Container = ensureBoundsProxy(container);
    const target: any = findMaskTarget(container, mask, boundsProxy);
    if (!target) return;

    if (!mask || (mask as any).destroyed) {
        mask = new Graphics();
        (container as any)[GRAPHICS_KEY] = mask;
    }

    // Ensure mask is a child of the container so it shares transforms and is in the same root.
    try {
        if (mask.parent && mask.parent !== container) mask.parent.removeChild(mask);
        if (!mask.parent) container.addChild(mask);
        (mask as any).eventMode = "none";
        mask.visible = true;
        mask.renderable = true;
        // NOTE: Mask rendering (stencil) is skipped when worldAlpha is 0, so keep alpha at 1.
        mask.alpha = 1;
        // avoid contributing to local-bounds
        mask.measurable = false;
    } catch {}

    // Compute UNMASKED local bounds for this clip-view (excluding proxy + mask).
    try {
        const prevTargetMask = target.mask;
        const prevProxyMeasurable = boundsProxy.measurable;
        boundsProxy.measurable = false;
        target.mask = null;

        _tmpBounds.clear();
        _tmpMatrix.identity();
        pixiGetLocalBounds(container, _tmpBounds, _tmpMatrix);

        target.mask = prevTargetMask;
        boundsProxy.measurable = prevProxyMeasurable;
    } catch {
        // fall back to whatever we have
        try {
            _tmpBounds.clear();
            _tmpMatrix.identity();
            pixiGetLocalBounds(container, _tmpBounds, _tmpMatrix);
        } catch {}
    }

    const b = _tmpBounds;
    const localW = Number((b as any).width ?? 0);
    const localH = Number((b as any).height ?? 0);
    if (!(localW > 0 && localH > 0)) return;

    // Keep unmasked bounds stable via a proxy child.
    updateProxyBounds(boundsProxy, b);

    // Compute mask center in local coords.
    const minX = Number((b as any).minX ?? (b as any).x ?? 0);
    const minY = Number((b as any).minY ?? (b as any).y ?? 0);
    const cx = minX + localW / 2;
    const cy = minY + localH / 2;

    // Compute circle radius in WORLD units (composition coords).
    const sX = Number((container as any).scale?.x ?? 1);
    const sY = Number((container as any).scale?.y ?? 1);
    const worldW = Math.abs(sX) * localW;
    const worldH = Math.abs(sY) * localH;
    const r =
        normalized === "circle"
            ? Math.max(1, Math.min(worldW, worldH) / 2)
            : Math.max(1, Math.min(worldW, worldH) * 0.45);

    // Counter-scale the mask so the circle stays a circle in world space even under non-uniform scaling.
    const invScaleX = sX !== 0 ? 1 / sX : 1;
    const invScaleY = sY !== 0 ? 1 / sY : 1;

    const nextState: MaskState = { shape: normalized, cx, cy, w: worldW, h: worldH, r, invScaleX, invScaleY };
    const prev: MaskState | null = (container as any)[STATE_KEY] ?? null;

    try {
        mask.position.set(cx, cy);
        mask.scale.set(invScaleX, invScaleY);
    } catch {}

    if (
        prev &&
        prev.shape === nextState.shape &&
        prev.cx === nextState.cx &&
        prev.cy === nextState.cy &&
        prev.w === nextState.w &&
        prev.h === nextState.h &&
        prev.r === nextState.r &&
        prev.invScaleX === nextState.invScaleX &&
        prev.invScaleY === nextState.invScaleY
    ) {
        try {
            if (target.mask !== mask) target.mask = mask;
        } catch {}
        return;
    }

    try {
        mask.clear();
        mask.beginFill(0xffffff, 1);
        if (normalized === "circle") {
            mask.drawCircle(0, 0, r);
        } else {
            mask.drawRoundedRect(-worldW / 2, -worldH / 2, worldW, worldH, r);
        }
        mask.endFill();
    } catch {}

    try {
        target.mask = mask;
    } catch {}

    try {
        (container as any)[STATE_KEY] = nextState;
    } catch {}
}
